'use client';

import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, HelpCircle, LogOut } from 'lucide-react';

import { cn, getInitials } from '@/lib/utils';
import { useGetCurrentUserQuery } from '@/state';
import { useGetUserCurrentOrganization, useTenant } from '@/hooks';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { buildSidebarNavigation, type AppSidebarProps } from '@/types';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';

import { getTenantLS, setTenantLS } from '@/lib';
import { ConfirmDialog, type ConfirmDialogConfig } from '@/components';
import { ThemeDropdown } from './theme-drop-down';
import { OrganizationDropdownCard } from './organization-dropdown-card';
import { toast } from 'sonner';

export function SideBar({
  className,
  onLogout,
  userOrganizations,
}: AppSidebarProps) {
  const { state: sidebarState, setOpen } = useSidebar();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { tenant } = useTenant();
  const { data: currentUser } = useGetCurrentUserQuery({ tenant });

  const { activeOrg, roles } = useGetUserCurrentOrganization({
    organizations: userOrganizations,
  });
  const {
    isSuperAdmin,
    isStudent,
    isTeacher,
    isSchoolAdmin,
    hasLMSModule,
    hasProjectModule,
    isLoading: permissionsLoading,
    isCompany,
  } = useClientPermissions();

  const hasTenant = !!tenant;
  const showMinimal = !hasTenant;
  const isEntryRoute =
    pathname === '/' || pathname === '/organization/register';

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmConfig, setConfirmConfig] =
    React.useState<ConfirmDialogConfig | null>(null);
  const [confirmLoading, setConfirmLoading] = React.useState(false);

  const normalizePath = React.useCallback((p: string) => {
    if (!p) return '/';
    return p.length > 1 ? p.replace(/\/+$/, '') : '/';
  }, []);

  const tenantPath = React.useCallback(
    (path: string) => {
      const clean = path.startsWith('/') ? path : `/${path}`;
      const latestTenant = getTenantLS() || tenant;
      return latestTenant ? `/${latestTenant}${clean}` : clean;
    },
    [tenant]
  );

  const isPathMatch = React.useCallback(
    (href: string): boolean => {
      const current = normalizePath(pathname);
      const target = normalizePath(tenantPath(href));
      return current === target || current.startsWith(`${target}/`);
    },
    [pathname, tenantPath, normalizePath]
  );

  const isActivePath = React.useCallback(
    (href: string, activeMatchers: string[] = []) => {
      if (href !== '#' && isPathMatch(href)) return true;
      return activeMatchers.some((matcher) => isPathMatch(matcher));
    },
    [isPathMatch]
  );
  const filteredNavItems = React.useMemo(
    () =>
      buildSidebarNavigation({
        isSuperAdmin,
        isPublicTenant: tenant === 'public',
        isStudent,
        isTeacher,
        isSchoolAdmin,
        hasLMSModule,
        hasProjectModule,
        isCompany,
      }),
    [isSuperAdmin, tenant, isStudent, isTeacher, isSchoolAdmin, hasLMSModule, hasProjectModule]
  );
  const navReady =
    !!activeOrg &&
    !permissionsLoading &&
    (isSuperAdmin || isStudent || isTeacher || isSchoolAdmin);

  const userProfile = currentUser?.profile;
  const userFullName = userProfile
    ? `${userProfile.first_name ?? ''} ${userProfile.last_name ?? ''}`.trim()
    : 'User';
  const userEmail = currentUser?.email ?? '';
  const userPhoto =
    userProfile?.photo_url ?? userProfile?.thumbnail ?? '/placeholder.svg';

  const go = React.useCallback(
    (href: string) => {
      if (href === '#') return;
      navigate(tenantPath(href));
    },
    [navigate, tenantPath]
  );
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({});
  const orgForDropdown =
    activeOrg ?? (userOrganizations && userOrganizations.length > 0
      ? userOrganizations[0]
      : null);
  const rolesForDropdown = (activeOrg?.user_roles ||
    orgForDropdown?.user_roles ||
    roles) as typeof roles;

  const navItemClass = (active: boolean) =>
    cn(
      'flex items-center gap-3 w-full h-10 px-3 rounded-xl',
      'text-[14px] font-medium tracking-wide transition-all duration-200',
      'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:w-10',
      active
        ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    );

  const openLogoutConfirm = () => {
    setConfirmConfig({
      title: 'Log out?',
      description: 'You will be signed out of your account.',
      confirmText: 'Logout',
      cancelText: 'Cancel',
      destructive: true,
      onConfirm: async () => {
        if (!onLogout) return;
        try {
          setConfirmLoading(true);
          onLogout!();
        } catch (error) {
          console.error('Logout failed:', error);
          toast.error('Failed to log out');
        } finally {
          setConfirmLoading(false);
        }
      },
    });
    setConfirmOpen(true);
  };

  return (
    <Sidebar collapsible="icon" className={cn('z-50 backdrop-blur-xl bg-sidebar/95 border-r border-sidebar-border/50', className)}>
      <SidebarHeader
        className={cn(hasTenant ? 'border-b pb-0' : '', 'border-sidebar-border/30 group-data-[collapsible=icon]:p-2')}
      >
        {hasTenant && (
          <div className="p-3 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
            <SidebarMenuButton
              tooltip="Profile"
              onClick={() => go('/profile')}
              className={cn(
                'w-full justify-start h-auto px-2 py-2 rounded-xl transition-all duration-200',
                'bg-muted/20 hover:bg-muted/40 border border-transparent hover:border-border/10',
                isActivePath('/profile') && 'border-primary/10 bg-primary/5',
                'group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0'
              )}
            >
              <div className="relative">
                <Avatar
                  className={cn(
                    'h-8 w-8 shrink-0 transition-transform duration-200',
                    isActivePath('/profile') && 'ring-2 ring-primary ring-offset-1 ring-offset-sidebar'
                  )}
                >
                  <AvatarImage src={userPhoto} alt={userFullName} />
                  <AvatarFallback className="bg-primary/10 text-primary font-bold">
                    {getInitials(userFullName)}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-sidebar bg-green-500" />
              </div>

              <div className="ml-3 min-w-0 group-data-[collapsible=icon]:hidden text-left flex-1">
                <div className="text-sm font-bold truncate leading-tight">
                  {userFullName}
                </div>
                <div className="text-[11px] text-muted-foreground truncate opacity-70">
                  {userEmail}
                </div>
              </div>
            </SidebarMenuButton>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent
        className={cn(
          showMinimal && 'opacity-60',
          'flex flex-col',
          'group-data-[collapsible=icon]:items-center'
        )}
      >
        <SidebarMenu className="p-2 w-full group-data-[collapsible=icon]:w-auto">
          {!showMinimal &&
            !isEntryRoute &&
            navReady &&
            filteredNavItems.map(({ id, href, title, icon: Icon, children, activeMatchers }) => {
              const childActive =
                children?.some((child) =>
                  isActivePath(child.href, child.activeMatchers ?? [])
                ) ?? false;
              const selfActive = isActivePath(href, activeMatchers ?? []);
              const hasChildren = !!children?.length;
              const isOpen = openGroups[id] ?? childActive;
              const isSidebarCollapsed = sidebarState === 'collapsed';
              const showParentActiveForChild =
                hasChildren && childActive && (isSidebarCollapsed || !isOpen);
              const active = hasChildren
                ? showParentActiveForChild || (selfActive && !childActive)
                : selfActive;

              return (
                <SidebarMenuItem key={id}>
                  <SidebarMenuButton
                    tooltip={title}
                    onClick={() => {
                      if (hasChildren) {
                        if (isSidebarCollapsed) {
                          setOpen(true);
                          setOpenGroups((prev) => ({ ...prev, [id]: true }));
                          return;
                        }
                        setOpenGroups((prev) => ({ ...prev, [id]: !isOpen }));
                        return;
                      }
                      go(href);
                    }}
                    className={navItemClass(active)}
                  >
                    <Icon
                      className={cn('h-5 w-5 shrink-0', active && 'scale-105')}
                    />
                    <span className="group-data-[collapsible=icon]:hidden">
                      {title}
                    </span>
                    {hasChildren && (
                      <ChevronDown
                        className={cn(
                          'ml-auto h-4 w-4 transition-transform group-data-[collapsible=icon]:hidden',
                          isOpen && 'rotate-180'
                        )}
                      />
                    )}
                  </SidebarMenuButton>
                  {hasChildren && isOpen && (
                    <div className="ml-8 mt-1 space-y-1 group-data-[collapsible=icon]:hidden">
                      {children.map((child) => {
                        const childIsActive = isActivePath(
                          child.href,
                          child.activeMatchers ?? []
                        );
                        const ChildIcon = child.icon;
                        return (
                          <SidebarMenuButton
                            key={child.id}
                            tooltip={child.title}
                            onClick={() => go(child.href)}
                            className={cn(
                              navItemClass(childIsActive),
                              'h-9 rounded-xl text-[13px]'
                            )}
                          >
                            {ChildIcon ? <ChildIcon className="h-4 w-4 shrink-0" /> : null}
                            <span>{child.title}</span>
                          </SidebarMenuButton>
                        );
                      })}
                    </div>
                  )}
                </SidebarMenuItem>
              );
            })}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border/30 bg-sidebar/40">
        <div
          className={cn(
            'p-3 space-y-3',
            'group-data-[collapsible=icon]:flex',
            'group-data-[collapsible=icon]:flex-col',
            'group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:p-2'
          )}
        >
          <SidebarMenu className="w-full group-data-[collapsible=icon]:w-auto">
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Help"
                onClick={() => { }}
                className={navItemClass(false)}
              >
                <HelpCircle className="h-5 w-5" />
                <span className="group-data-[collapsible=icon]:hidden">
                  Help
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <ThemeDropdown className={navItemClass(false)} />
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Logout"
                onClick={openLogoutConfirm}
                className={navItemClass(false)}
              >
                <LogOut className="h-5 w-5" />
                <span className="group-data-[collapsible=icon]:hidden">
                  Logout
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>

          {!!orgForDropdown && (
            <OrganizationDropdownCard
              organizations={userOrganizations ?? []}
              activeOrg={orgForDropdown}
              roles={rolesForDropdown ?? []}
              onSelect={(org) => {
                const schema = org.client?.schema_name;
                if (!schema) {
                  toast.message('Organization not found');
                  return;
                }
                setTenantLS(schema);
                navigate(`/${schema}/dashboard`);
              }}
            />
          )}
        </div>
      </SidebarFooter>

      <ConfirmDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        config={confirmConfig}
        loading={confirmLoading}
      />

      <SidebarRail />
    </Sidebar>
  );
}
